/**
 * Host Utilities
 *
 * Single source of truth for distinguishing the teacher host
 * (scimodeling.com) from the student join host (join.scimodeling.com).
 * Both hostnames are served by the same container/bundle — the split is
 * determined entirely by window.location.hostname, checked once here
 * instead of scattered throughout the app.
 */

import { config } from '@/config';

const DEV_OVERRIDE_KEY = 'pauls-models-join-host-override';

function getJoinHostname(): string {
  try {
    return new URL(config.joinUrl || 'https://join.scimodeling.com').hostname;
  } catch {
    return 'join.scimodeling.com';
  }
}

let cachedIsJoinHost: boolean | null = null;

/**
 * Whether the app is currently running on the student join subdomain.
 * The hostname can't change without a full page reload, so this is
 * computed once per page load and cached — router setup and components
 * stay consistent for the lifetime of the session.
 *
 * For local development, append `?join=1` to the URL once to simulate
 * the join host; the choice is remembered in sessionStorage so it
 * survives client-side navigation.
 */
export function isJoinHost(): boolean {
  if (cachedIsJoinHost !== null) return cachedIsJoinHost;

  if (typeof window === 'undefined') {
    cachedIsJoinHost = false;
    return cachedIsJoinHost;
  }

  const { hostname, search } = window.location;

  if (hostname === getJoinHostname() || hostname.startsWith('join.')) {
    cachedIsJoinHost = true;
  } else if (new URLSearchParams(search).get('join') === '1') {
    window.sessionStorage.setItem(DEV_OVERRIDE_KEY, '1');
    cachedIsJoinHost = true;
  } else if (window.sessionStorage.getItem(DEV_OVERRIDE_KEY) === '1') {
    cachedIsJoinHost = true;
  } else {
    cachedIsJoinHost = false;
  }

  return cachedIsJoinHost;
}

/** Build a full join-host URL, optionally deep-linking straight to a code. */
export function getJoinUrl(code?: string): string {
  const base = config.joinUrl || `https://${getJoinHostname()}`;
  return code ? `${base}/${code}` : base;
}

/** Build the teacher/apex host URL (used when linking back from the join host). */
export function getTeacherUrl(): string {
  return config.appUrl || 'https://scimodeling.com';
}
